import type { ReactNode } from 'react';
import { createElement } from 'react';
import { LuRoute, LuZap, LuHouse, LuStethoscope } from 'react-icons/lu';
import type { ModeloTarefa } from '../services/ModeloTarefaService';
import { etapasDoMiolo } from './etapas';

/**
 * A biblioteca de trilhos prontos (docs/propostas/tarefas-padrao.html §5, tela 4).
 * Vive no front como o catálogo dos Agentes prontos: escolher um copia o trilho
 * para a empresa (POST ModeloTarefa/Aplicar); depois a empresa edita o dela.
 * Mudar um trilho aqui não mexe em quem já o copiou.
 *
 * Etapas do funil que recebem tarefa: 2 Em qualificação, 4 Proposta enviada,
 * 5 Em negociação. Os desfechos de sucesso têm o nome do estado que alcançam.
 */
export interface TrilhoPronto {
    chave: string;
    nome: string;
    descricao: string;
    icone: ReactNode;
    /** Rótulo extra no cartão, tipo "1 dia útil". */
    destaque?: string;
    etapas: ModeloTarefa[];
}

const perda = (ordem: number) => ({ rotulo: 'Venda perdida', ordem, acao: 'lose' as const });

export const TRILHOS_PRONTOS: TrilhoPronto[] = [
    {
        chave: 'venda-consultiva',
        nome: 'Venda consultiva',
        descricao: 'Qualifica, envia proposta, cobra retorno e fecha. Reagenda por "sem contato" contando tentativas.',
        icone: createElement(LuRoute, { size: 16 }),
        destaque: 'dias úteis',
        etapas: [
            { statusId: 2, titulo: 'Qualificar o lead', tipoTarefaNome: 'Ligação', prazoDiasUteis: 2, ativo: true, desfechos: [
                { rotulo: 'Qualificado, proposta enviada', ordem: 0, acao: 'move', statusDestinoId: 4 },
                { rotulo: 'Sem contato', ordem: 1, acao: 'reschedule', diasReagendamento: 2 },
                perda(2),
            ] },
            { statusId: 4, titulo: 'Cobrar retorno da proposta', tipoTarefaNome: 'Ligação', prazoDiasUteis: 3, ativo: true, desfechos: [
                { rotulo: 'Cliente respondeu, negociando', ordem: 0, acao: 'move', statusDestinoId: 5 },
                { rotulo: 'Sem retorno', ordem: 1, acao: 'reschedule', diasReagendamento: 3 },
                perda(2),
            ] },
            { statusId: 5, titulo: 'Fechar a negociação', tipoTarefaNome: 'Reunião', prazoDiasUteis: 2, ativo: true, desfechos: [
                { rotulo: 'Ganho', ordem: 0, acao: 'move', statusDestinoId: 3 },
                { rotulo: 'Reagendar', ordem: 1, acao: 'reschedule', diasReagendamento: 2 },
                perda(2),
            ] },
        ],
    },
    {
        chave: 'venda-rapida',
        nome: 'Venda rápida',
        descricao: 'Para ciclo curto: contato e fechamento, prazo de 1 dia útil, sem cobrança de proposta.',
        icone: createElement(LuZap, { size: 16 }),
        destaque: '1 dia útil',
        etapas: [
            { statusId: 2, titulo: 'Fazer contato e apresentar a oferta', tipoTarefaNome: 'Ligação', prazoDiasUteis: 1, ativo: true, desfechos: [
                { rotulo: 'Interessado, negociando', ordem: 0, acao: 'move', statusDestinoId: 5 },
                { rotulo: 'Sem contato', ordem: 1, acao: 'reschedule', diasReagendamento: 1 },
                perda(2),
            ] },
            { statusId: 4, titulo: 'Confirmar recebimento da oferta', tipoTarefaNome: 'Ligação', prazoDiasUteis: 1, ativo: false, desfechos: [
                { rotulo: 'Recebeu, negociando', ordem: 0, acao: 'move', statusDestinoId: 5 },
                perda(1),
            ] },
            { statusId: 5, titulo: 'Fechar a venda', tipoTarefaNome: 'Ligação', prazoDiasUteis: 1, ativo: true, desfechos: [
                { rotulo: 'Ganho', ordem: 0, acao: 'move', statusDestinoId: 3 },
                { rotulo: 'Pediu para ligar depois', ordem: 1, acao: 'reschedule', diasReagendamento: 1 },
                perda(2),
            ] },
        ],
    },
    {
        chave: 'imobiliaria',
        nome: 'Imobiliária',
        descricao: 'Qualifica o perfil, agenda visita, cobra retorno da visita e negocia. "Visita feita" reagenda para a próxima.',
        icone: createElement(LuHouse, { size: 16 }),
        etapas: [
            { statusId: 2, titulo: 'Qualificar o perfil e agendar visita', tipoTarefaNome: 'Ligação', prazoDiasUteis: 2, ativo: true, desfechos: [
                { rotulo: 'Visita agendada, proposta em mãos', ordem: 0, acao: 'move', statusDestinoId: 4 },
                { rotulo: 'Sem contato', ordem: 1, acao: 'reschedule', diasReagendamento: 2 },
                { rotulo: 'Ainda pesquisando', ordem: 2, acao: 'reschedule', diasReagendamento: 7 },
                perda(3),
            ] },
            { statusId: 4, titulo: 'Cobrar retorno da visita', tipoTarefaNome: 'Ligação', prazoDiasUteis: 2, ativo: true, desfechos: [
                { rotulo: 'Gostou, negociando', ordem: 0, acao: 'move', statusDestinoId: 5 },
                { rotulo: 'Visita feita, quer ver outro', ordem: 1, acao: 'reschedule', diasReagendamento: 3 },
                { rotulo: 'Sem retorno', ordem: 2, acao: 'reschedule', diasReagendamento: 3 },
                perda(3),
            ] },
            { statusId: 5, titulo: 'Fechar a negociação', tipoTarefaNome: 'Reunião', prazoDiasUteis: 3, ativo: true, desfechos: [
                { rotulo: 'Ganho', ordem: 0, acao: 'move', statusDestinoId: 3 },
                { rotulo: 'Aguardando documentação', ordem: 1, acao: 'reschedule', diasReagendamento: 5 },
                perda(2),
            ] },
        ],
    },
    {
        chave: 'clinica',
        nome: 'Clínica',
        descricao: 'Confirma interesse, agenda avaliação e confirma comparecimento. Falta é "sem contato" com prazo de 1 dia.',
        icone: createElement(LuStethoscope, { size: 16 }),
        etapas: [
            { statusId: 2, titulo: 'Confirmar interesse e agendar avaliação', tipoTarefaNome: 'Ligação', prazoDiasUteis: 1, ativo: true, desfechos: [
                { rotulo: 'Avaliação agendada', ordem: 0, acao: 'move', statusDestinoId: 4 },
                { rotulo: 'Sem contato', ordem: 1, acao: 'reschedule', diasReagendamento: 1 },
                perda(2),
            ] },
            { statusId: 4, titulo: 'Confirmar comparecimento à avaliação', tipoTarefaNome: 'Ligação', prazoDiasUteis: 1, ativo: true, desfechos: [
                { rotulo: 'Compareceu, orçamento em mãos', ordem: 0, acao: 'move', statusDestinoId: 5 },
                { rotulo: 'Faltou, reagendar', ordem: 1, acao: 'reschedule', diasReagendamento: 1 },
                perda(2),
            ] },
            { statusId: 5, titulo: 'Fechar o tratamento', tipoTarefaNome: 'Reunião', prazoDiasUteis: 2, ativo: true, desfechos: [
                { rotulo: 'Ganho', ordem: 0, acao: 'move', statusDestinoId: 3 },
                { rotulo: 'Pensando, retornar', ordem: 1, acao: 'reschedule', diasReagendamento: 3 },
                perda(2),
            ] },
        ],
    },
];

/** "Começar do zero": uma caixa vazia por etapa do miolo do funil da empresa, para o editor. */
export const trilhoVazio = (): ModeloTarefa[] => etapasDoMiolo().map(statusId => ({
    statusId, titulo: '', prazoDiasUteis: 2, ativo: false, desfechos: [],
}));
